import { useSyncExternalStore } from 'react';
import { EN_EXACT, EN_PATTERNS } from './en';

// English / Spanish (23 Oct 2026). The screens are the prototype's, written in Spanish: Spanish is the source and the
// default, and English is a translation applied where text reaches the screen (the template runtime's el()/text(), and
// the hand-written sign-in screens). Fixed texts by exact match; texts built from values (dates, amounts, names) by
// pattern. Numbers, money and dates keep the league's Spanish format in both languages. The choice is remembered in
// this browser.

export type Lang = 'es' | 'en';
const KEY = 'zenit.lang';

function stored(): Lang {
  try { return localStorage.getItem(KEY) === 'en' ? 'en' : 'es'; } catch { return 'es'; }
}

let current: Lang = typeof window === 'undefined' ? 'es' : stored();
const subs = new Set<() => void>();

export const getLang = (): Lang => current;

export function setLang(l: Lang): void {
  if (l === current) return;
  current = l;
  try { localStorage.setItem(KEY, l); } catch { /* private mode: this visit only */ }
  if (typeof document !== 'undefined') document.documentElement.lang = l;
  subs.forEach(f => f());
}

/** Re-renders the caller when the language changes. */
export function useLang(): Lang {
  return useSyncExternalStore(f => { subs.add(f); return () => subs.delete(f); }, getLang, getLang);
}

/** Texts asked for in English that have no translation (tests: every screen must leave none). */
export const missing = new Set<string>();

/** `s` in the current language; surrounding whitespace is kept. */
export function tr(s: string): string {
  if (current === 'es' || !s) return s;
  const m = /^(\s*)([\s\S]*?)(\s*)$/.exec(s)!;
  return m[1] + en(m[2]) + m[3];
}

/** No words to translate: numbers, dates, amounts, codes (USD, BME, SAN), units ("u"). */
const languageFree = (s: string) => (s.match(/\p{L}+/gu) ?? []).every(w => w === 'u' || w === 'x' || (w === w.toUpperCase() && w.length <= 6));

function en(core: string): string {
  if (!core || languageFree(core)) return core;
  const hit = EN_EXACT[core] ?? pattern(core);
  if (hit !== undefined) return hit;
  // Lines the screens join with " · " (symbol · market · currency · type; type · amount · fee…): part by part.
  if (core.includes(' · ')) return core.split(' · ').map(en).join(' · ');
  missing.add(core);
  return core;
}

function pattern(s: string): string | undefined {
  for (const [re, f] of EN_PATTERNS) { const m = re.exec(s); if (m) return f(m); }
  return undefined;
}
